import * as turf from '@turf/turf'
import type { Feature, FeatureCollection, GeoJsonProperties } from 'geojson'

import { sumBy } from '../number'
import { isNil } from '../object'
import {
  applyEditsToPolygons,
  DisjointCollection,
  normalizeCollection
} from './collection-operations'
import { Polygon } from './feature-operations'
import type { PolyLikeFeature, PolyLikeGeometry } from './types'

export const formatZones = (
  zones: {
    max: number
    key: string
    min: number
  }[],
  formatNumbers: (value: number) => string
) =>
  zones
    .toSorted((a, b) => a.min - b.min)
    .map(({ min, max, key }) => ({
      key,
      name: `${formatNumbers(min)} - ${formatNumbers(max)}`
    }))

/**
 * Generates an array of numeric ranges by starting at the middle index
 * and alternating outward to the right and left.
 *
 * The middle index is included first, then higher indices, then lower ones,
 * creating a balanced ordering around the center (e.g. 3, 4, 2, 5, 1).
 *
 * Each index is converted into a [min, max] range using the provided step
 * size, and tagged as either "high" or "low" depending on which side of the
 * midpoint it came from.
 *
 * Useful for layouts or priority ordering where values should expand
 * symmetrically from the center rather than sequentially.
 *
 * This is used in generating prescriptions so that the zones on the end of
 * the spectrum (e.g. high weed severity) get the priority when being zoned
 * Doing them last lays them over top
 *
 * e.g generateAlternatingRanges(5, 10, { low: 0 })
 * [
 * { min: 20, max: 30, type: "low"  }, // i = 3 (middle)
 * { min: 30, max: 40, type: "high" }, // i = 4
 * { min: 10, max: 20, type: "low"  }, // i = 2
 * { min: 40, max: 50, type: "high" }, // i = 5
 * { min: 0,  max: 10, type: "low"  }  // i = 1
 * ]
 */
export function generateAlternatingRanges(n: number, step: number, body: { low: number }) {
  const ranges = Array.from({ length: n }, (_, index) => ({
    min: body.low + (index - 1) * step,
    max: body.low + index * step
  }))
  return alternateFromMiddle(ranges).map(({ item, type }) => ({ ...item, type }))
}

function alternateFromMiddle<T>(items: T[]) {
  const n = items.length
  const mid = Math.ceil(n / 2)
  let low = mid - 1
  let high = mid + 1

  const order: { i: number; type: 'high' | 'low' }[] = []

  // include the middle number first
  order.push({ i: mid, type: 'low' }) // or 'high' if you want the middle to start high

  let toggle = true
  // alternate outward from the middle
  while (low >= 1 || high <= n) {
    if (toggle && high <= n) order.push({ i: high++, type: 'high' })
    else if (!toggle && low >= 1) order.push({ i: low--, type: 'low' })
    toggle = !toggle
  }

  return order.map(({ i, type }) => ({ item: items[i - 1], type }))
}

export type ZoneRange = { min: number; max: number }

export const zoneRates = ({
  base,
  overlays
}: {
  base: PolyLikeFeature
  overlays: PolyLikeFeature[]
}) => {
  const keys: string[] = []

  const features = [base, ...overlays].reduce<PolyLikeFeature[]>((acc, feat, curIdx) => {
    const zoneId = crypto.randomUUID()

    feat.id = zoneId
    feat.properties = {
      zone: zoneId
    }
    keys.push(zoneId)

    if (curIdx !== 0) {
      const edits = DisjointCollection.insert(feat, turf.featureCollection(acc), {
        shouldUnion: (existing, incoming) => existing.properties?.zone === incoming.properties?.zone
      })

      if (edits.length > 0) {
        return applyEditsToPolygons(acc, edits)
      }
    }
    acc.push(feat)
    return acc
  }, [])
  return { keys, features }
}

export const placeZones = ({
  boundary,
  baseIndex,
  zones
}: {
  boundary: FeatureCollection<PolyLikeGeometry>
  baseIndex: number
  zones: PolyLikeFeature[][]
}) => {
  const normalizedBoundary = normalizeCollection(boundary)

  if (isNil(normalizedBoundary)) {
    throw new Error('Unable to Normalize Boundary')
  }

  const placed = zones.flatMap((features, index) => {
    if (index === baseIndex || features.length === 0) return []
    const merged = normalizeCollection(turf.featureCollection(features))
    if (isNil(merged)) return []

    merged.id = `zone-${index}`
    const clipped = DisjointCollection.clip(merged, boundary)
    return isNil(clipped) ? [] : [{ index, feature: clipped }]
  })

  const { keys: placedKeys, features } = zoneRates({
    base: normalizedBoundary,
    overlays: placed.map(({ feature }) => feature)
  })
  const placedIndices = [baseIndex, ...placed.map(({ index }) => index)]
  const keyByIndex = new Map(
    placedIndices.map((index, position) => [index, placedKeys[position]] as const)
  )

  const keys = zones.map((_, index) => keyByIndex.get(index))
  const acresOf = (key: string | undefined) =>
    turf.convertArea(
      sumBy(
        features.filter((feature) => feature.properties?.zone === key),
        (feature) => turf.area(feature)
      ),
      'meters',
      'acres'
    )

  return {
    features,
    keys,
    acres: keys.map((key) => (key === undefined ? 0 : acresOf(key)))
  }
}

export const smoothAndClipRate = async (
  feat: FeatureCollection<PolyLikeGeometry, GeoJsonProperties> | Feature<PolyLikeGeometry>,
  normalizedBoundary: PolyLikeFeature,
  boundaryCollection: FeatureCollection<PolyLikeGeometry, GeoJsonProperties>
) => {
  const featureCollection =
    feat.type === 'FeatureCollection' ? feat : turf.featureCollection([feat])
  if (featureCollection.features.length === 0) {
    return undefined
  }

  const multiPolygon = normalizeCollection(featureCollection)

  if (isNil(multiPolygon)) {
    return undefined
  }

  const smoothed = normalizeCollection(
    Polygon.polygonSmooth(
      turf.simplify(multiPolygon, {
        mutate: true,
        tolerance: 0.00005
      }),
      { boundary: normalizedBoundary }
    )
  )

  if (isNil(smoothed)) {
    return undefined
  }

  smoothed.id = `smoothed-${Date.now()}`

  return DisjointCollection.clip(smoothed, boundaryCollection)
}
